import { useState, useEffect, useRef, RefObject } from 'react';

interface UseScrollVisibilityReturn {
  refs: RefObject<HTMLDivElement | null>[];
  visibleItems: boolean[];
}

export function useScrollVisibility(
  count: number = 1,
  threshold: number = 0.15
): UseScrollVisibilityReturn {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(() => 
    Array(count).fill(false)
  );
  const refs = useRef<RefObject<HTMLDivElement | null>[]>([]);

  // Initialize refs array
  if (refs.current.length !== count) {
    refs.current = Array(count)
      .fill(null)
      .map((_, i) => refs.current[i] || { current: null });
  }

  useEffect(() => {
    const observers = refs.current.map((ref, index) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
              setVisibleItems((prev) => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }
          });
        },
        { threshold: [0, threshold] }
      );

      if (ref.current) {
        observer.observe(ref.current);
      }

      return observer;
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [count, threshold]);

  return { refs: refs.current, visibleItems };
}

export default useScrollVisibility;


